import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import AlertComponent from "../../components/AlertComponent";

export function VioletSwitch({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 ${
        checked ? "bg-violet-600" : "bg-slate-300"
      } ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform translate-y-1 ${
          checked ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );
}

export function TextField({ label, value, onChange, placeholder, type = "text", disabled, required, autoFocus, helper, mono }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        autoFocus={autoFocus}
        autoComplete="off"
        className={`w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400
                   focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent focus:bg-white transition
                   disabled:opacity-60 disabled:cursor-not-allowed ${mono ? "font-mono" : ""}`}
      />
      {helper && <p className="mt-1 text-[11px] text-slate-500">{helper}</p>}
    </div>
  );
}

export function NumberField({ label, value, onChange, placeholder, disabled, min, helper }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
        {label}
      </label>
      <input
        type="number"
        value={value ?? 0}
        onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
        placeholder={placeholder}
        disabled={disabled}
        min={min}
        className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400
                   focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent focus:bg-white transition
                   disabled:opacity-60 disabled:cursor-not-allowed tabular-nums"
      />
      {helper && <p className="mt-1 text-[11px] text-slate-500">{helper}</p>}
    </div>
  );
}

export function TextareaField({ label, value, onChange, placeholder, disabled, rows = 3, helper }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
        {label}
      </label>
      <textarea
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        spellCheck={false}
        className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400
                   focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent focus:bg-white transition
                   disabled:opacity-60 disabled:cursor-not-allowed font-mono resize-y"
      />
      {helper && <p className="mt-1 text-[11px] text-slate-500">{helper}</p>}
    </div>
  );
}

export function ActiveToggleRow({ checked, onChange, disabled, label = "Ativo", description = "Desative para excluir deste catálogo sem perder o registro." }) {
  return (
    <label className={`flex items-start justify-between gap-4 p-4 rounded-xl ring-1 transition cursor-pointer ${
      checked ? "bg-violet-50/60 ring-violet-200" : "bg-slate-50/50 ring-slate-200"
    }`}>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-800">{label}</p>
        {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
      </div>
      <VioletSwitch checked={checked} onChange={onChange} disabled={disabled} />
    </label>
  );
}

export function ArrayEditor({ items, onChange, placeholder, disabled }) {
  const list = items || [];
  const addItem = () => onChange([...list, ""]);
  const removeItem = (idx) => onChange(list.filter((_, i) => i !== idx));
  const updateItem = (idx, value) => onChange(list.map((v, i) => (i === idx ? value : v)));

  return (
    <div className="space-y-2">
      {list.length === 0 && (
        <p className="text-xs text-slate-400 italic">Nenhum item. Clique em "Adicionar" para começar.</p>
      )}
      {list.map((item, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <input
            type="text"
            value={item ?? ""}
            onChange={(e) => updateItem(idx, e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            className="flex-1 rounded-xl border border-slate-200 bg-slate-50/50 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent focus:bg-white transition font-mono disabled:opacity-60"
          />
          <button
            type="button"
            onClick={() => removeItem(idx)}
            disabled={disabled}
            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
            aria-label="Remover"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addItem}
        disabled={disabled}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-violet-700 bg-violet-50 hover:bg-violet-100 rounded-lg transition-colors disabled:opacity-50"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
        </svg>
        Adicionar
      </button>
    </div>
  );
}

function initials(name) {
  const base = (name || "?").trim();
  const parts = base.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
}

export default function CatalogCrud({
  title,
  eyebrow = "Administração",
  description,
  endpoint,
  itemSubtitle,
  blank,
  snapshotFrom,
  buildPayload,
  validate,
  renderForm,
  createSuccessMsg = "Registro criado com sucesso",
  saveSuccessMsg = "Alterações salvas com sucesso",
  deleteSuccessMsg = "Registro removido com sucesso",
  deleteConfirmTitle = "Remover registro",
  deleteConfirmText = "Tem certeza que deseja remover este registro? Esta ação pode ser revertida apenas pelo administrador.",
  duplicateEnabled = false,
  duplicateSuccessMsg = "Registro duplicado com sucesso",
  emptyStateTitle = "Nenhum registro cadastrado",
  emptyStateHint = "Clique em Novo para começar.",
  newButtonLabel = "Novo",
  selectPromptTitle = "Selecione um item",
  selectPromptHint = "Ou clique em Novo para criar.",
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(null);
  const [original, setOriginal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [duplicating, setDuplicating] = useState(false);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API}${endpoint}`)
      .then((res) => setItems(res.data || []))
      .catch((err) => {
        const mensagem = err?.response?.data?.message || err?.message || `Erro ao carregar ${title.toLowerCase()}`;
        AlertComponent("Erro", mensagem, { icon: "error" });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  const selected = useMemo(
    () => items.find((m) => m.id === selectedId) || null,
    [items, selectedId]
  );

  const isDirty = useMemo(() => {
    if (!form || !original) return false;
    return JSON.stringify(form) !== JSON.stringify(original);
  }, [form, original]);

  const applyItem = (item) => {
    const snap = snapshotFrom(item);
    setCreating(false);
    setSelectedId(item.id);
    setForm(snap);
    setOriginal(snap);
  };

  const enterCreateMode = () => {
    setCreating(true);
    setSelectedId(null);
    setForm(structuredClone(blank));
    setOriginal(structuredClone(blank));
  };

  const confirmDiscardOrSave = async () => {
    return AlertComponent(
      "Alterações não salvas",
      creating
        ? "As informações do novo registro serão perdidas. Deseja salvar ou descartar as alterações?"
        : "As informações deste registro foram alteradas. Deseja salvar ou descartar as alterações?",
      {
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Salvar",
        cancelButtonText: "Descartar",
        confirmButtonColor: "#7c3aed",
        cancelButtonColor: "#64748b",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showCloseButton: false,
        reverseButtons: true,
      },
      null,
      false
    );
  };

  const save = async (item, snap, isCreate) => {
    const erro = validate ? validate(snap, isCreate) : null;
    if (erro) {
      AlertComponent("Atenção", erro, { icon: "warning" });
      return null;
    }
    setSaving(true);
    try {
      const payload = buildPayload ? buildPayload(snap, isCreate) : snap;

      if (isCreate) {
        const res = await axios.post(`${process.env.REACT_APP_API}${endpoint}`, payload);
        const created = res.data || {};
        const enriched = { ...snap, ...created };
        setItems((prev) => [enriched, ...prev]);
        AlertComponent("Sucesso", createSuccessMsg, { icon: "success" });
        return enriched;
      }

      await axios.put(`${process.env.REACT_APP_API}${endpoint}/${item.id}`, payload);
      const updated = { ...item, ...snap };
      setItems((prev) => prev.map((m) => (m.id === item.id ? updated : m)));
      setOriginal(snap);
      AlertComponent("Sucesso", saveSuccessMsg, { icon: "success" });
      return updated;
    } catch (err) {
      const mensagem = err?.response?.data?.message || err?.message || "Erro ao salvar";
      AlertComponent("Erro", mensagem, { icon: "error" });
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selected || deleting) return;
    const result = await AlertComponent(
      deleteConfirmTitle,
      deleteConfirmText,
      {
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sim, remover",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#dc2626",
        cancelButtonColor: "#64748b",
        reverseButtons: true,
      },
      null,
      false
    );
    if (!result?.isConfirmed) return;

    setDeleting(true);
    try {
      await axios.delete(`${process.env.REACT_APP_API}${endpoint}/${selected.id}`);
      setItems((prev) => prev.filter((m) => m.id !== selected.id));
      setSelectedId(null);
      setForm(null);
      setOriginal(null);
      AlertComponent("Sucesso", deleteSuccessMsg, { icon: "success" });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        const serverMsg = err?.response?.data?.message;
        AlertComponent(
          "Registro em uso",
          serverMsg || "Este registro está sendo utilizado por um ou mais ambientes e não pode ser removido.",
          { icon: "warning" }
        );
      } else {
        const mensagem = err?.response?.data?.message || err?.message || "Erro ao remover registro";
        AlertComponent("Erro", mensagem, { icon: "error" });
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleDuplicate = async () => {
    if (!duplicateEnabled || !selected || duplicating) return;
    if (isDirty && form) {
      const result = await confirmDiscardOrSave();
      if (result?.isConfirmed) {
        const saved = await save(selected, form, false);
        if (!saved) return;
      } else if (result?.dismiss) {
        applyItem(selected);
      } else {
        return;
      }
    }

    setDuplicating(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API}${endpoint}/${selected.id}/duplicate`);
      const duplicated = res.data || {};
      setItems((prev) => [duplicated, ...prev]);
      applyItem(duplicated);
      AlertComponent("Sucesso", duplicateSuccessMsg, { icon: "success" });
    } catch (err) {
      const mensagem = err?.response?.data?.message || err?.message || "Erro ao duplicar registro";
      AlertComponent("Erro", mensagem, { icon: "error" });
    } finally {
      setDuplicating(false);
    }
  };

  const handleSelect = async (m) => {
    if (!creating && m.id === selectedId) return;
    if (isDirty && form) {
      const result = await confirmDiscardOrSave();
      if (result?.isConfirmed) {
        const saved = await save(selected, form, creating);
        if (!saved) return;
      } else if (!result?.dismiss) {
        return;
      }
    }
    applyItem(m);
  };

  const handleNew = async () => {
    if (creating && !isDirty) return;
    if (isDirty && form) {
      const result = await confirmDiscardOrSave();
      if (result?.isConfirmed) {
        const saved = await save(selected, form, creating);
        if (!saved) return;
      } else if (!result?.dismiss) {
        return;
      }
    }
    enterCreateMode();
  };

  const handleSave = async () => {
    if (!form || !isDirty) return;
    if (creating) {
      const created = await save(null, form, true);
      if (created) applyItem(created);
    } else if (selected) {
      await save(selected, form, false);
    }
  };

  const update = (keyOrPath, value) => {
    setForm((prev) => {
      if (!prev) return prev;
      if (!keyOrPath.includes(".")) return { ...prev, [keyOrPath]: value };
      const keys = keyOrPath.split(".");
      const result = { ...prev };
      let cursor = result;
      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        cursor[key] = Array.isArray(cursor[key]) ? [...cursor[key]] : { ...(cursor[key] ?? {}) };
        cursor = cursor[key];
      }
      cursor[keys[keys.length - 1]] = value;
      return result;
    });
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div
        className="rounded-2xl p-6 sm:p-8 mb-6 relative overflow-hidden text-white"
        style={{ background: "linear-gradient(135deg, #1e1b4b 0%, #3b0764 100%)" }}
      >
        <div className="absolute -top-10 -right-10 w-52 h-52 rounded-full opacity-10" style={{ background: "radial-gradient(circle,#a78bfa,transparent)" }} />
        <div className="absolute -bottom-12 -left-6 w-40 h-40 rounded-full opacity-10" style={{ background: "radial-gradient(circle,#c4b5fd,transparent)" }} />
        <div className="relative">
          <p className="text-purple-300 text-sm font-medium mb-1">{eyebrow}</p>
          <h1 className="text-2xl sm:text-3xl font-bold">{title}</h1>
          {description && <p className="text-purple-300 text-sm mt-1">{description}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] gap-6">
        {/* Esquerda: lista */}
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/60 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-violet-500" />
              <h2 className="text-base font-bold text-slate-800">{title}</h2>
              {!loading && (
                <span className="ml-1 px-2 py-0.5 text-xs font-semibold bg-violet-100 text-violet-700 rounded-full">
                  {items.length}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={handleNew}
              disabled={saving || deleting}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white shadow-sm hover:shadow transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: "linear-gradient(135deg,#7c3aed,#6d28d9)" }}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
              </svg>
              {newButtonLabel}
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-violet-300 border-t-violet-600 rounded-full animate-spin" />
            </div>
          ) : items.length === 0 && !creating ? (
            <div className="text-center py-16 px-6">
              <p className="text-slate-500 font-medium">{emptyStateTitle}</p>
              <p className="text-slate-400 text-sm mt-1">{emptyStateHint}</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100 overflow-y-auto max-h-[calc(100vh-280px)]">
              {creating && (
                <li>
                  <div className="w-full text-left flex items-center gap-3 px-5 py-3.5 bg-violet-50 ring-1 ring-inset ring-violet-200">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center text-white shrink-0 ring-2 ring-violet-300 ring-offset-2"
                      style={{ background: "linear-gradient(135deg,#a78bfa,#7c3aed)" }}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-violet-900 truncate">
                          {(form?.name || "").trim() || "Novo registro"}
                        </p>
                        {isDirty && (
                          <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-orange-100 text-orange-700 ring-1 ring-orange-200">
                            Não salvo
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              )}
              {items.map((m) => {
                const isSelected = m.id === selectedId;
                const isDirtyHere = isSelected && isDirty;
                const subtitle = itemSubtitle ? itemSubtitle(m) : null;
                return (
                  <li key={m.id}>
                    <button
                      type="button"
                      onClick={() => handleSelect(m)}
                      className={`w-full text-left flex items-center gap-3 px-5 py-3.5 transition-colors ${
                        isSelected
                          ? "bg-violet-50 ring-1 ring-inset ring-violet-200"
                          : "hover:bg-slate-50"
                      }`}
                    >
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center text-white text-xs font-bold shrink-0 ${
                          isSelected ? "ring-2 ring-violet-300 ring-offset-2" : ""
                        }`}
                        style={{ background: "linear-gradient(135deg,#a78bfa,#7c3aed)" }}
                      >
                        {initials(m.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className={`font-semibold truncate ${isSelected ? "text-violet-900" : "text-slate-800"}`}>
                            {m.name}
                          </p>
                          {m.isActive === false && (
                            <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-slate-100 text-slate-600 ring-1 ring-slate-200">
                              Inativo
                            </span>
                          )}
                          {isDirtyHere && (
                            <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-orange-100 text-orange-700 ring-1 ring-orange-200">
                              Não salvo
                            </span>
                          )}
                        </div>
                        {subtitle && (
                          <p className="text-xs text-slate-500 truncate mt-0.5 font-mono">{subtitle}</p>
                        )}
                      </div>
                      <svg className={`w-4 h-4 shrink-0 ${isSelected ? "text-violet-500" : "text-slate-300"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Direita: form */}
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/60 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-2 h-2 rounded-full bg-violet-500 shrink-0" />
              <h2 className="text-base font-bold text-slate-800 shrink-0">
                {creating ? "Novo" : "Detalhes"}
              </h2>
              {!creating && selected && (
                <span className="ml-1 text-xs text-slate-500 truncate">· {selected.name}</span>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {isDirty && (
                <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-orange-100 text-orange-700 ring-1 ring-orange-200">
                  Não salvo
                </span>
              )}
              {!creating && selected && duplicateEnabled && (
                <button
                  type="button"
                  onClick={handleDuplicate}
                  disabled={duplicating || deleting || saving}
                  className="p-1.5 text-slate-400 hover:text-violet-700 hover:bg-violet-50 rounded-lg transition-colors disabled:opacity-50"
                  aria-label="Duplicar"
                  title="Duplicar"
                >
                  {duplicating ? (
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <rect x="8" y="8" width="11" height="11" rx="2" ry="2" strokeWidth={2} />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 16H4a2 2 0 01-2-2V5a2 2 0 012-2h9a2 2 0 012 2v1" />
                    </svg>
                  )}
                </button>
              )}
              {!creating && selected && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting || saving || duplicating}
                  className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                  aria-label="Remover"
                  title="Remover"
                >
                  {deleting ? (
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  )}
                </button>
              )}
            </div>
          </div>

          {!form ? (
            <div className="flex-1 text-center py-20 px-6">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
                </svg>
              </div>
              <p className="text-slate-500 font-medium mb-1">{selectPromptTitle}</p>
              <p className="text-slate-400 text-sm">{selectPromptHint}</p>
            </div>
          ) : (
            <>
              <div className="overflow-y-auto flex-1 p-6 space-y-5">
                {renderForm({ form, update, saving: saving || duplicating, creating })}
              </div>

              <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/60 flex items-center justify-between gap-3 shrink-0">
                <p className="text-xs text-slate-500 min-w-0 truncate">
                  {isDirty ? "Há alterações pendentes de salvamento." : "Nenhuma alteração pendente."}
                </p>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!isDirty || saving || duplicating}
                  className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold text-white shadow-lg shadow-purple-900/20 hover:shadow-purple-900/40 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none shrink-0"
                  style={{ background: "linear-gradient(135deg,#7c3aed,#6d28d9)" }}
                >
                  {saving ? (
                    <>
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                      Salvando...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Salvar alterações
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
